import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { clientApi } from '@tankef/api-client';
import { logger } from '@tankef/env';

import { usePrimaryAccount } from '@/domains/wallet/accounts/api';

export type MovementType = 'income' | 'expense';
export type MovementStatus = 'pending' | 'completed' | 'failed' | 'cancelled';
export type MovementKind = 'cargo' | 'abono';
export type MovementDirection = 'debit' | 'credit';

/**
 * Desglose de comisión/IVA de un crédito SPEI IN (bruto → comisión + IVA → neto).
 * Nullable: solo lo traen los créditos SPEI IN. Los `*Cents` son la fuente de
 * verdad (enteros); los strings vienen pre-formateados para mostrar.
 */
export interface FeeBreakdown {
  grossCents: number;
  gross: string;
  commissionCents: number;
  commission: string;
  ivaCents: number;
  iva: string;
  feeTotalCents: number;
  feeTotal: string;
  netCents: number;
  net: string;
}

export interface WalletBalance {
  availableCents: number;
  heldCents: number;
  totalCents: number;
  currency: string;
  accountId: string;
  clabe: string;
}

export interface MovementCounterparty {
  name?: string;
  bankName?: string;
  clabe?: string;
}

export interface Movement {
  id: string;
  type: MovementType;
  status: MovementStatus;
  description: string;
  reference?: string;
  amountCents: number;
  currency: string;
  /** Fecha contable (`occurred_at`; `created_at` si el BE aún no la manda). */
  date: Date;
  /** Cuenta a la que pertenece la fila y su saldo (`account_id`). */
  accountId?: string;
  /** La otra parte: beneficiario en un cargo, ordenante en un abono (`counterparty`). */
  counterparty?: MovementCounterparty;
  /** Clave de rastreo SPEI (`spei_tracking_key`). Sin ella el movimiento no fue SPEI. */
  trackingKey?: string;
  /** Tipo de operación del BE (`operation_type`, 15 valores fijos; desconocido = `other`). */
  operationType?: string;
  /** Referencia numérica SPEI (`numeric_reference`); sólo llega en `spei_in`. */
  numericReference?: string;
  journalEntryId?: string;
  entryNumber?: string;
  entryDate?: string;
  movementType?: MovementKind;
  direction?: MovementDirection;
  amountFormatted?: string;
  folio?: string;
  rawStatus?: string;
  statusLabel?: string;
  entryType?: string;
  feeBreakdown?: FeeBreakdown | null;
  /** Saldo tras el movimiento, calculado por el ledger del BE. `null` = no disponible. */
  balanceAfterCents?: number | null;
  /**
   * Monto a mostrar en la tabla cuando difiere de `amountCents` (abono con su
   * desglose separado: aquí va el bruto). `amountCents` sigue siendo el del BE
   * y es el que usan el comprobante y los totales.
   */
  displayAmountCents?: number;
  /** Abono cuyo `feeBreakdown` ya se pinta como filas propias (sin ícono de desglose). */
  feeExpanded?: boolean;
  /** Fila derivada de `expandFeeBreakdown`: no es un asiento del BE. */
  derived?: { parentId: string; kind: FeeRowKind };
}

export type FeeRowKind = 'commission' | 'iva';

export interface MovementsFilter {
  startDate?: string;
  endDate?: string;
  type?: MovementType;
  status?: MovementStatus;
  direction?: MovementDirection;
  entryType?: string;
}

const STATUS_MAP: Record<string, MovementStatus> = {
  liquidado: 'completed',
  completado: 'completed',
  completed: 'completed',
  pendiente: 'pending',
  pending: 'pending',
  fallido: 'failed',
  failed: 'failed',
  cancelado: 'cancelled',
  cancelled: 'cancelled',
};

function parseCurrencyFromAmount(amount: string): string {
  const parts = amount?.trim().split(/\s+/);
  return parts && parts.length > 1 ? (parts[parts.length - 1] ?? 'MXN') : 'MXN';
}

/**
 * Fecha de la fila: la contable (`occurred_at`), que es la misma con la que el
 * BE acumula `balance_after_cents` y filtra por fecha. Un asiento creado un día
 * y publicado otro aparece en el de publicación. Si el BE aún no la manda, cae
 * a `created_at`.
 */
export function mapMovementDate(raw: { occurred_at?: string | null; created_at: string }): Date {
  return new Date(raw.occurred_at || raw.created_at);
}

/**
 * Moneda de la fila: la de su cuenta (`currency_code`). En `/wallet/movements`
 * cada fila trae el saldo de SU cuenta, así que el saldo se pinta con esta
 * moneda. Sin el campo, se deduce del monto formateado como antes.
 */
export function mapMovementCurrency(raw: {
  currency_code?: string | null;
  amount: string;
}): string {
  return raw.currency_code?.trim() || parseCurrencyFromAmount(raw.amount);
}

/**
 * Saldo posterior al movimiento (`balance_after_cents`). Sólo lo calcula el BE
 * con su ledger: el FE no lo reconstruye porque con paginación, filtros o
 * movimientos rechazados/retenidos daría cifras falsas. Acepta string
 * numérico (Rails serializa algunos decimales así); cualquier otra cosa —o
 * un BE que todavía no lo manda— es `null` y la tabla muestra "—".
 *
 * Un valor presente pero que no es centavos enteros (p. ej. centavos
 * fraccionarios) también cae a `null`, pero deja un `warn`: así un cambio de
 * contrato del BE se ve en el log y no sólo como una columna vacía. Sólo se
 * registra la forma, nunca el monto.
 */
export function mapBalanceAfterCents(raw: number | string | null | undefined): number | null {
  if (raw === null || raw === undefined || raw === '') return null;
  const value = typeof raw === 'number' ? raw : Number(raw);
  if (Number.isSafeInteger(value)) return value;
  logger.warn('[wallet] balance_after_cents no es un entero de centavos; se muestra "—"', {
    type: typeof raw,
  });
  return null;
}

/**
 * Separa el desglose de cada abono en filas propias. De arriba hacia abajo,
 * como se pintan en la tabla:
 *
 *   IVA       −IVA       saldo (el del BE, tras el neto)
 *   Comisión  −comisión  saldo + IVA
 *   Abono     +bruto     saldo + IVA + comisión
 *
 * El grupo sigue el orden del resto de la tabla, de más reciente a más
 * antiguo, en el orden en que se aplican los cargos: el abono abajo, encima la
 * comisión y arriba el IVA que se calcula sobre ella. Leído de abajo hacia
 * arriba, cada fila = la de abajo menos su monto, y la de arriba (IVA) queda
 * con el saldo real del BE.
 *
 * Es PRESENTACIÓN, sólo para la tabla: las filas derivadas no son asientos del
 * BE (no se descargan ni cuentan en totales) y el abono conserva `amountCents`
 * = neto para el comprobante; el bruto va en `displayAmountCents`. Por eso no
 * se aplica en los hooks: el Dashboard también usa `useMovements` para contar.
 *
 * Sólo expande si el desglose cuadra (bruto − comisión − IVA = neto del
 * movimiento); si no, deja la fila como viene en vez de mostrar números que
 * no suman. Todo abono con desglose lleva sus dos filas aunque valgan $0.00
 * (así todos los abonos se leen igual); sin `fee_breakdown` no se inventan.
 *
 * EXCEPCIÓN DELIBERADA a "el saldo sólo viene del ledger": el BE asienta el
 * abono neto como un solo movimiento, así que los saldos de las filas Abono y
 * Comisión los compone el FE a partir del `balance_after_cents` real (que queda en
 * la fila IVA). Decisión 2026-10-02 (review del PR #167): se mantiene como
 * puente hasta `display_lines` (RFC 0037, aún [PROPUESTO]), donde el BE mandará
 * cada línea con su propio saldo; al llegar, esta función se retira.
 */
export function expandFeeBreakdown(movements: Movement[]): Movement[] {
  return movements.flatMap(m => {
    const fb = m.feeBreakdown;
    if (m.type !== 'income' || !fb) return [m];
    const cuadra =
      fb.commissionCents + fb.ivaCents === fb.feeTotalCents &&
      fb.grossCents - fb.feeTotalCents === m.amountCents;
    if (!cuadra) return [m];

    const after = m.balanceAfterCents ?? null;
    const plus = (cents: number) => (after === null ? null : after + cents);

    const derivedRow = (kind: FeeRowKind, cents: number, balance: number | null): Movement => ({
      ...m,
      id: `${m.id}:${kind}`,
      type: 'expense',
      amountCents: cents,
      displayAmountCents: undefined,
      feeBreakdown: null,
      feeExpanded: undefined,
      entryType: kind,
      balanceAfterCents: balance,
      derived: { parentId: m.id, kind },
    });

    return [
      derivedRow('iva', fb.ivaCents, after),
      derivedRow('commission', fb.commissionCents, plus(fb.ivaCents)),
      {
        ...m,
        displayAmountCents: fb.grossCents,
        feeExpanded: true,
        balanceAfterCents: plus(fb.feeTotalCents),
      },
    ];
  });
}

const presentOrUndefined = (value?: string | null) => value?.trim() || undefined;

/**
 * Mapea la contraparte del movimiento. `null`, ausente o con todos sus campos
 * vacíos → `undefined`: el comprobante pinta "—" en esas filas.
 */
export function mapMovementCounterparty(
  raw: { name: string | null; bank_name: string | null; clabe: string | null } | null | undefined
): MovementCounterparty | undefined {
  if (!raw) return undefined;
  const counterparty: MovementCounterparty = {
    name: presentOrUndefined(raw.name),
    bankName: presentOrUndefined(raw.bank_name),
    clabe: presentOrUndefined(raw.clabe),
  };
  return counterparty.name || counterparty.bankName || counterparty.clabe
    ? counterparty
    : undefined;
}

/** `spei_tracking_key` vacía o `null` → `undefined` (el movimiento no fue SPEI). */
export function mapMovementTrackingKey(raw: string | null | undefined): string | undefined {
  return presentOrUndefined(raw);
}

/** `operation_type` vacío o `null` → `undefined`. */
export function mapMovementOperationType(raw: string | null | undefined): string | undefined {
  return presentOrUndefined(raw);
}

/**
 * Referencia numérica SPEI (`numeric_reference`, sólo en `spei_in`). Nunca cae a
 * `reference`: el BE confirmó que es un id interno.
 */
export function mapMovementNumericReference(raw: string | null | undefined): string | undefined {
  return presentOrUndefined(raw);
}

/**
 * Mapea el `fee_breakdown` (snake_case del backend) al modelo camelCase.
 * Aditivo y retrocompatible: si viene `null`/`undefined`, devuelve `null`.
 */
export function mapFeeBreakdown(
  raw:
    | {
        gross_cents: number;
        gross: string;
        commission_cents: number;
        commission: string;
        iva_cents: number;
        iva: string;
        fee_total_cents: number;
        fee_total: string;
        net_cents: number;
        net: string;
      }
    | null
    | undefined
): FeeBreakdown | null {
  if (!raw) return null;
  return {
    grossCents: raw.gross_cents,
    gross: raw.gross,
    commissionCents: raw.commission_cents,
    commission: raw.commission,
    ivaCents: raw.iva_cents,
    iva: raw.iva,
    feeTotalCents: raw.fee_total_cents,
    feeTotal: raw.fee_total,
    netCents: raw.net_cents,
    net: raw.net,
  };
}

export function useTransactionLimits() {
  return useQuery({
    queryKey: ['wallet', 'transaction-limits'],
    queryFn: () => clientApi.transactionLimits(),
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });
}

export function useTransactionLimitsUsage() {
  return useQuery({
    queryKey: ['wallet', 'transaction-limits', 'usage'],
    queryFn: () => clientApi.transactionLimitsUsage(),
    staleTime: 30_000,
    // No refetchInterval — BalanceChannel `balance_updated` empuja
    // payload.limits_usage. Invalidación en realtimeInvalidations.ts.
  });
}

export function useUpdateTransactionLimits() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      daily_limit_cents?: number;
      monthly_limit_cents?: number;
      single_transaction_limit_cents?: number;
    }) => clientApi.updateTransactionLimits(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallet', 'transaction-limits'] });
      queryClient.invalidateQueries({ queryKey: ['wallet', 'transaction-limits', 'usage'] });
    },
  });
}

export function useAccountClabe(accountId: string) {
  return useQuery({
    queryKey: ['account', accountId, 'clabe'],
    queryFn: () => clientApi.accountClabe(accountId),
    enabled: !!accountId,
    staleTime: 10 * 60_000,
    gcTime: 60 * 60_000,
    // 404 = CLABE not registered yet — expected for new accounts, don't hammer the server.
    // Updates arrive via WS account_updated → RealtimeGateProvider invalidates this key.
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) return false;
      return failureCount < 2;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}

export function useAccountHolds(accountId: string, status?: string) {
  return useQuery({
    queryKey: ['account', accountId, 'holds', status],
    queryFn: () => clientApi.accountHolds(accountId, status ? { status } : undefined),
    enabled: !!accountId,
    staleTime: 30_000,
    // No refetchInterval — BalanceChannel emite hold_*.
  });
}

export function useWalletBalance() {
  const primaryAccountQuery = usePrimaryAccount();

  const balanceQuery = useQuery({
    queryKey: ['wallet', 'balance', primaryAccountQuery.data?.id],
    queryFn: async (): Promise<WalletBalance> => {
      const accountId =
        primaryAccountQuery.data?.id ?? (await primaryAccountQuery.refetch()).data?.id;

      if (!accountId) {
        throw new Error('Primary account not found');
      }

      const data = await clientApi.accountBalance(accountId);
      return {
        availableCents: data.available_cents ?? data.available_balance_cents ?? 0,
        heldCents: data.held_cents ?? data.hold_balance_cents ?? data.held_balance_cents ?? 0,
        totalCents: data.total_cents ?? data.ledger_balance_cents ?? 0,
        currency: data.currency ?? data.currency_code ?? 'MXN',
        accountId: data.account_id ?? accountId,
        clabe: data.clabe,
      };
    },
    enabled: !!primaryAccountQuery.data?.id,
    staleTime: 30_000,
  });

  return {
    ...balanceQuery,
    isLoading: primaryAccountQuery.isLoading || balanceQuery.isLoading,
    isPending: primaryAccountQuery.isPending || balanceQuery.isPending,
    isFetching: primaryAccountQuery.isFetching || balanceQuery.isFetching,
  };
}

export function useMovements(filters?: MovementsFilter, page = 1, pageSize = 10) {
  return useQuery({
    queryKey: ['wallet', 'movements', filters, page, pageSize],
    queryFn: async () => {
      const response = await clientApi.movements({
        page,
        per_page: pageSize,
        start_date: filters?.startDate,
        end_date: filters?.endDate,
        direction: (filters?.direction as 'debit' | 'credit' | undefined) || undefined,
        entry_type: filters?.entryType || undefined,
      });

      const movements: Movement[] = response.data.movements.map(m => {
        const type: MovementType = m.movement_type === 'abono' ? 'income' : 'expense';
        const status = STATUS_MAP[m.status] ?? 'pending';

        return {
          id: m.id,
          type,
          status,
          description: m.description,
          reference: m.reference ?? undefined,
          amountCents: m.amount_cents,
          currency: mapMovementCurrency(m),
          date: mapMovementDate(m),
          accountId: m.account_id ?? undefined,
          journalEntryId: m.journal_entry_id,
          entryNumber: m.entry_number,
          entryDate: m.entry_date,
          movementType: m.movement_type,
          direction: m.direction,
          amountFormatted: m.amount,
          folio: m.folio,
          rawStatus: m.status,
          statusLabel: m.status_label,
          entryType: m.entry_type,
          feeBreakdown: mapFeeBreakdown(m.fee_breakdown),
          balanceAfterCents: mapBalanceAfterCents(m.balance_after_cents),
          counterparty: mapMovementCounterparty(m.counterparty),
          trackingKey: mapMovementTrackingKey(m.spei_tracking_key),
          operationType: mapMovementOperationType(m.operation_type),
          numericReference: mapMovementNumericReference(m.numeric_reference),
        };
      });

      const total = response.meta.total;
      const perPage = response.meta.per_page;

      return {
        movements,
        meta: {
          page: response.meta.page,
          pageSize: perPage,
          total,
          totalPages: perPage > 0 ? Math.max(1, Math.ceil(total / perPage)) : 1,
        },
      };
    },
    staleTime: 30_000,
  });
}

export function useWalletSummary() {
  return useQuery({
    queryKey: ['wallet', 'summary'],
    queryFn: () => clientApi.walletSummary(),
    staleTime: 60_000,
  });
}

export function useDownloadWalletReport() {
  return useMutation({
    mutationFn: (params: {
      type: 'movements' | 'statements';
      startDate: string;
      endDate: string;
      format?: 'pdf' | 'csv' | 'xlsx';
    }) =>
      clientApi.downloadWalletReport({
        type: params.type,
        start_date: params.startDate,
        end_date: params.endDate,
        format: params.format,
      }),
  });
}
